import api from '../../services/api';


const STRATEGY_API_URL = '/strategies';

const StrategyService = {
  /**
   * Create a new strategy
   * @param {Object} payload - Strategy data ({ name, description, config: { nodes, edges, ... } })
   * @returns {Promise} API Response
   */
  createStrategy: async (payload) => {
    return api.post(STRATEGY_API_URL, payload);
  },

  /**
   * Get all strategies
   * @param {Object} params - Query params { page, page_size, status }
   * @returns {Promise} List of strategies
   */
  getStrategies: async (params = {}) => {
    return api.get(STRATEGY_API_URL, { params });
  },

  /**
   * Get specific strategy by ID
   * @param {string} id - Strategy ID
   * @returns {Promise} Strategy details
   */
  getStrategyById: async (id) => {
    return api.get(`${STRATEGY_API_URL}/${id}`);
  },

  /**
   * Get versions for a strategy
   * @param {string} id - Strategy ID
   * @returns {Promise} List of versions
   */
  getStrategyVersions: async (id) => {
    return api.get(`${STRATEGY_API_URL}/${id}/versions`);
  },

  /**
   * Get specific strategy version by ID
   * @param {string} strategyId - Strategy ID
   * @param {string} versionId - Version ID
   * @returns {Promise} Version details
   */
  getStrategyVersionById: async (strategyId, versionId) => {
    return api.get(`${STRATEGY_API_URL}/${strategyId}/versions/${versionId}`);
  },

  /**
   * Update strategy metadata
   * @param {string} id - Strategy ID
   * @param {Object} data - Update data { name, description, status }
   * @returns {Promise} Updated strategy
   */
  updateStrategy: async (id, data) => {
    return api.put(`${STRATEGY_API_URL}/${id}`, data);
  },

  /**
   * Create new strategy version (Save Configuration)
   * @param {string} id - Strategy ID
   * @param {Object} config - Strategy config ({ nodes, edges })
   * @param {string} description - Change description
   * @param {string} [parentVersionId] - Parent version ID for branching
   * @returns {Promise} New version
   */
  saveVersion: async (id, config, description, parentVersionId = null) => {
    const payload = {
      config,
      change_description: description
    };
    if (parentVersionId) {
      payload.parent_version_id = parentVersionId;
    }
    return api.post(`${STRATEGY_API_URL}/${id}/versions`, payload);
  },

  /**
   * Delete (archive) a strategy
   * @param {string} id - Strategy ID
   * @returns {Promise} Success response
   */
  deleteStrategy: async (id) => {
    return api.delete(`${STRATEGY_API_URL}/${id}`);
  },

  /**
   * Activate a specific version (Revert/Switch)
   * @param {string} strategyId 
   * @param {string} versionId 
   * @returns {Promise} Updated strategy
   */
  activateVersion: async (strategyId, versionId) => {
    return api.put(`${STRATEGY_API_URL}/${strategyId}/versions/${versionId}/activate`);
  },

  /**
   * Get available OHLC fields metadata from TimescaleDB schema introspection
   * @returns {Promise} { data: { standard: [], calculated: [] } }
   */
  getMetadataFields: async () => {
    // Call via API Gateway (proxied to strategy-management)
    return api.get(`${STRATEGY_API_URL}/meta/fields`);
  },

  /**
   * Deploy a strategy version
   * @param {string} strategyId - Strategy ID
   * @param {string} versionId - Version to deploy
   * @param {string} mode - 'paper' or 'live'
   * @param {Object|null} driftResolution - per-account-order-settings R6.2.2: on a redeploy whose
   *   RUNNING allocation settings differ from this version's, the operator's chosen resolution
   *   `{ resolution: 'carry_over'|'use_new', diff_snapshot_hash }` (server-validated against the
   *   CURRENT diff). Omitted on a first deploy / no-drift redeploy; a drifted redeploy without it
   *   is rejected 409 DEPLOY_DRIFT_UNRESOLVED (the FE then prompts + re-submits with the choice).
   * @param {Object|null} ackParams - R5.6/R4.4.2 deployment-readiness ack — deploy-readiness-checklist
   *   `{ ack: true, acknowledged_item_ids: string[], acknowledged_snapshot_hash: string }`. Required
   *   when deploying LIVE with a failing RECOMMENDED readiness item and all REQUIRED items passing;
   *   omitted otherwise. A missing/stale ack on a LIVE deploy that needs one 422s with
   *   `error.code === "READINESS_ACK_REQUIRED"` and `error.details.readiness` /
   *   `error.details.readiness_snapshot_hash` — the caller re-submits using THOSE fresh values, not
   *   any earlier reactive-panel fetch (N-2 two-phase handshake, 02-design.md).
   * @returns {Promise} Deployment object
   */
  deployStrategy: async (strategyId, versionId, mode, driftResolution = null, ackParams = null) => {
    return api.post(`${STRATEGY_API_URL}/${strategyId}/deploy`, {
      version_id: versionId,
      mode,
      ...(driftResolution ? { drift_resolution: driftResolution } : {}),
      ...(ackParams?.ack ? {
        ack: true,
        acknowledged_item_ids: ackParams.acknowledged_item_ids || [],
        acknowledged_snapshot_hash: ackParams.acknowledged_snapshot_hash || null,
      } : {}),
    });
  },

  /**
   * R5.2 — evaluate deployment-readiness for the CURRENT (possibly unsaved) in-editor graph.
   * NO persistence — a stateless preview call, debounced by the caller. Routed via the gateway
   * (auth:8000) → strategy-management, same as every other call in this service layer.
   * @param {string} strategyId - Strategy ID (must already exist — ad-hoc/unsaved graphs still
   *   evaluate against an existing strategy's ownership + backtest-status context).
   * @param {Object} config - Current in-editor graph, SAME shape as `saveVersion`'s config param
   *   (`{ nodes, edges, general, instruments, orders }`).
   * @param {string} mode - 'paper' or 'live'
   * @returns {Promise} { data: { data: { mode, items: ReadinessItem[], readiness_snapshot_hash } } }
   */
  evaluateReadiness: async (strategyId, config, mode) => {
    return api.post(`${STRATEGY_API_URL}/${strategyId}/readiness`, { config, mode });
  },

  /**
   * Stop a running deployment
   * @param {string} strategyId - Strategy ID
   * @param {string} deploymentId - Deployment to stop
   * @returns {Promise} Updated deployment
   */
  stopDeployment: async (strategyId, deploymentId) => {
    return api.post(`${STRATEGY_API_URL}/${strategyId}/deployments/${deploymentId}/stop`);
  },

  /**
   * Get the active deployment for a strategy
   * @param {string} strategyId - Strategy ID
   * @returns {Promise} Active deployment or { data: null }
   */
  getActiveDeployment: async (strategyId) => {
    return api.get(`${STRATEGY_API_URL}/${strategyId}/deployments/active`);
  },

  /**
   * Check if a strategy version has open positions via OMS
   * @param {string} strategyInstanceId - Full strategy_instance_id (user_id:strategy_id:version_id:instrument_id:timeframe)
   * @returns {Promise} Position data (check side !== 'FLAT' for open)
   */
  getPositionForVersion: async (strategyInstanceId) => {
    return api.get(`/positions/${encodeURIComponent(strategyInstanceId)}`);
  },

  /**
   * 50-6 §B — fetch the per-node visual-override patch map for a strategy (Indicators tab).
   * @param {string} strategyId - Strategy ID
   * @returns {Promise} { data: { data: { overrides, etag, can_edit } } } — `etag` lives in the
   *   BODY (D38), never the `ETag` response header (no `expose_headers` configured cross-origin).
   */
  getVisualOverrides: async (strategyId) => {
    return api.get(`${STRATEGY_API_URL}/${strategyId}/visual-overrides`);
  },
};

/**
 * 50-8 — persist the per-node visual-override patch map. NAMED export, deliberately NOT a member of
 * the default `StrategyService` object: the ESLint `no-restricted-imports` rule (package.json) lets
 * only `src/hooks/useVisualOverrides.js` import it, so the autosave hook is the one writer (AC1.4).
 * `ifMatch` is a REQUIRED parameter (no optional/undefined branch) — D38/AC-N3: `If-Match` is sent
 * on EVERY PUT, never conditionally; the gateway's validator 428s a PUT with no `If-Match`.
 * @param {string} strategyId - Strategy ID
 * @param {Object} overrides - full replace map { [nodeId]: patch }
 * @param {string} ifMatch - current etag (body-sourced, D38)
 * @param {Object} [options]
 * @param {boolean} [options.keepalive] - 50-24: deliver the write so it survives page unload. Same
 *   `api` instance, same Authorization interceptor, same If-Match; the only difference is the axios
 *   fetch adapter with `fetchOptions.keepalive` (axios >= 1.7). Callers keep the body <= 64 KiB.
 * @returns {Promise} { data: { data: { overrides, etag } } }
 */
export const saveVisualOverrides = async (strategyId, overrides, ifMatch, { keepalive = false } = {}) => {
  return api.put(
    `${STRATEGY_API_URL}/${strategyId}/visual-overrides`,
    { overrides },
    {
      headers: { 'If-Match': ifMatch },
      ...(keepalive ? { adapter: 'fetch', fetchOptions: { keepalive: true } } : {}),
    },
  );
};

export default StrategyService;
